import mapboxgl from "mapbox-gl";
import { mapActive } from "../utils/map-utils";

export class BBoxSelection {
  map: mapboxgl.Map;
  onDrag?: (bbox: [mapboxgl.Point, mapboxgl.Point]) => void;
  onClick?: (point: mapboxgl.Point) => void;
  listenerRemovers: (() => void)[] = [];

  constructor(
    map: mapboxgl.Map,
    callbacks: {
      onDrag?: (bbox: [mapboxgl.Point, mapboxgl.Point]) => void;
      onClick?: (point: mapboxgl.Point) => void;
    },
  ) {
    this.map = map;
    this.onDrag = callbacks.onDrag;
    this.onClick = callbacks.onClick;
  }

  disable() {
    this.listenerRemovers.map((fn) => fn());
    this.listenerRemovers = [];
  }

  enable() {
    const _this = this;
    const map = this.map;

    if (!mapActive(map)) return;

    const canvas = map.getCanvasContainer();

    const multiSelect: {
      start?: mapboxgl.Point;
      current?: mapboxgl.Point;
      box?: HTMLDivElement;
    } = {};

    canvas.addEventListener("mousedown", mouseDown, true);
    this.listenerRemovers.push(() => {
      canvas.removeEventListener("mousedown", mouseDown, true);
    });

    // Return the xy coordinates of the mouse position
    function mousePos(e: MouseEvent) {
      const rect = canvas.getBoundingClientRect();
      return new mapboxgl.Point(
        e.clientX - rect.left - canvas.clientLeft,
        e.clientY - rect.top - canvas.clientTop,
      );
    }

    const bboxAreaInPixels = ([p1, p2]: [mapboxgl.Point, mapboxgl.Point]) => {
      return Math.abs(p1.x - p2.x) * Math.abs(p1.y - p2.y);
    };

    function cleanup() {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("keydown", onKeyDown);

      if (multiSelect.box) {
        multiSelect.box!.parentNode!.removeChild(multiSelect.box);
        multiSelect.box = undefined;
      }

      map.dragPan.enable();
    }

    function mouseDown(e: MouseEvent) {
      // Continue the rest of the function if the shiftkey is pressed.
      if (!(e.shiftKey && e.button === 0)) return;

      // Disable default drag zooming when the shift key is held down.
      map.dragPan.disable();

      // Call functions for the following events
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("keydown", onKeyDown);
      _this.map.once("mouseup", onMouseUp);

      // Capture the first xy coordinates
      multiSelect.start = mousePos(e);
    }

    async function onMouseMove(e: MouseEvent) {
      // Capture the ongoing xy coordinates
      multiSelect.current = mousePos(e);

      // Append the box element if it doesnt exist
      if (!multiSelect.box) {
        multiSelect.box = document.createElement("div");
        multiSelect.box.classList.add("mapbox-boxdraw");
        canvas.appendChild(multiSelect.box);
      }

      const minX = Math.min(multiSelect.start!.x, multiSelect.current.x);
      const maxX = Math.max(multiSelect.start!.x, multiSelect.current.x);
      const minY = Math.min(multiSelect.start!.y, multiSelect.current.y);
      const maxY = Math.max(multiSelect.start!.y, multiSelect.current.y);

      // Adjust width and xy position of the box element ongoing
      multiSelect.box.style.transform = `translate(${minX}px, ${minY}px)`;
      multiSelect.box.style.width = maxX - minX + "px";
      multiSelect.box.style.height = maxY - minY + "px";
    }

    async function onMouseUp(e: mapboxgl.MapMouseEvent) {
      cleanup();

      const bbox = [multiSelect.start!, e.point] as [
        mapboxgl.Point,
        mapboxgl.Point,
      ];
      const isDrag = bboxAreaInPixels(bbox) > 100;

      if (isDrag) {
        _this.onDrag?.(bbox);
      } else {
        _this.onClick?.(e.point);
      }
    }

    async function onKeyDown(e: KeyboardEvent) {
      // If the ESC key is pressed
      if (e.code === "0x0001") cleanup();
    }
  }
}
